import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Home, MapPin, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AdminTabs } from "../../components/admin/AdminTabs";
import { useConfirm } from "../../components/ConfirmProvider";

const PAGE = 50;

export default function AdminJobs() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  // If the backend honors pagination, each response has <= PAGE items (server-paged).
  // Otherwise it returns everything and we paginate/filter on the client (fallback).
  const [serverPaged, setServerPaged] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/admin/jobs?limit=${PAGE}&skip=${page * PAGE}&q=${encodeURIComponent(term)}`)
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : [];
        setServerPaged(arr.length <= PAGE);
        setJobs(arr);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, term]);

  const remove = async (j) => {
    if (!(await confirm({ description: `${t("confirm.deleteDesc")}` }))) return;
    try { await api.delete(`/jobs/${j.id}`); toast.success(t("admin.jobsMgmt.deleted")); setJobs((prev) => prev.filter((x) => x.id !== j.id)); }
    catch (err) { toast.error(t("common.error")); }
  };

  const onSearch = (e) => { e.preventDefault(); setPage(0); setTerm(q.trim()); };

  // Compute what to render depending on server vs client pagination.
  const filtered = serverPaged
    ? jobs
    : jobs.filter((j) => (`${j.title} ${j.city} ${j.state}`).toLowerCase().includes(term.toLowerCase()));
  const pageItems = serverPaged ? jobs : filtered.slice(page * PAGE, (page + 1) * PAGE);
  const hasNext = serverPaged ? jobs.length === PAGE : (page + 1) * PAGE < filtered.length;
  const hasPrev = page > 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("admin.jobsMgmt.title")}</h1>
      <AdminTabs />

      <form onSubmit={onSearch} className="relative mb-4" data-testid="ajobs-search-form">
        <Search className="w-5 h-5 text-stone-muted absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          data-testid="ajobs-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("admin.users.search")}
          className="w-full pl-12 pr-4 py-3 rounded-2xl border border-stone-border bg-white focus:outline-none focus:ring-2 focus:ring-brand"
        />
      </form>

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : pageItems.length === 0 ? (
        <div className="py-12 text-center text-stone-muted" data-testid="ajobs-empty">{t("admin.jobsMgmt.empty")}</div>
      ) : (
        <>
          <div className="space-y-3" data-testid="ajobs-list">
            {pageItems.map((j) => (
              <div key={j.id} className="bg-white rounded-2xl border border-stone-border shadow-sm p-4 flex items-center justify-between gap-3" data-testid="ajob-item">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-stone-text truncate">{j.title}</h3>
                    {j.offers_housing && <Home className="w-4 h-4 text-housing" />}
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${j.status === "active" ? "bg-housing/15 text-housing" : "bg-stone-bg text-stone-muted"}`}>{j.status}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-stone-muted mt-1">
                    <MapPin className="w-3.5 h-3.5" /> {j.city}, {j.state} · {j.views || 0} {t("admin.jobsMgmt.views")}
                  </div>
                </div>
                <button data-testid="ajob-delete-btn" onClick={() => remove(j)} className="p-2 rounded-lg hover:bg-red-50 text-stone-muted hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-6" data-testid="ajobs-pagination">
            <button data-testid="ajobs-prev" disabled={!hasPrev} onClick={() => setPage((p) => Math.max(0, p - 1))} className="flex items-center gap-1 px-4 py-2 rounded-xl border border-stone-border bg-white font-semibold text-stone-sub disabled:opacity-40 hover:border-brand hover:text-brand transition-colors">
              <ChevronLeft className="w-4 h-4" /> {t("admin.jobsMgmt.prev")}
            </button>
            <span className="text-sm text-stone-muted">{t("admin.jobsMgmt.page")} {page + 1}</span>
            <button data-testid="ajobs-next" disabled={!hasNext} onClick={() => setPage((p) => p + 1)} className="flex items-center gap-1 px-4 py-2 rounded-xl border border-stone-border bg-white font-semibold text-stone-sub disabled:opacity-40 hover:border-brand hover:text-brand transition-colors">
              {t("admin.jobsMgmt.next")} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
